/**
 * AppFooter — bottom capsule with news ticker, matrix toggle, Stack Sats button,
 * donation link, and data attribution.
 * Extracted from Index.tsx for maintainability.
 */

import { useState, useMemo, useRef, useLayoutEffect, MutableRefObject } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playExitMatrix } from '@/lib/audio/matrixSfx';
import type { NewsItem } from '@/hooks/useBuddyBackend';
import type { NetworkState } from '@/types/game';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';
import type { GamePhase } from '@/hooks/useStackSatsGame';
import NewsSheet from '@/components/NewsSheet';
import BuddyThoughtTicker from '@/components/BuddyThoughtTicker';

interface AppFooterProps {
  network: NetworkState;
  matrixMode: boolean;
  newsItems?: NewsItem[];
  macro: MacroDisplayBundle;
  stackSatsPhase: GamePhase;
  stackSatsLoading: boolean;
  worldDimming: boolean;
  sceneRef: MutableRefObject<HTMLDivElement | null>;
  onMatrixEnter: () => void;
  onMatrixExit: () => void;
  onStackSatsStart: () => void;
  onSetStackSatsLoading: (v: boolean) => void;
  onShowDonation: () => void;
  onRefreshNews: () => Promise<void>;
  thoughtTickerProps?: {
    mood: string;
    weather: string;
    networkState: string;
    blockState: string;
    priceState: string;
    verdict: string;
    sentimentBucket?: string | null;
    activeSignature: string | null;
    blocksToHalving?: number;
    hashrateChange7d?: number;
    lightningVitalityScore: number;
    footerLine: string;
    macroState: string;
    seasonState?: string;
  };
}

// ── News Ticker ──

function NewsTicker({ newsItems, onShowNews }: { newsItems?: NewsItem[]; onShowNews: () => void }) {
  const items = newsItems;
  const isLoading = items === undefined;
  const isEmpty = !isLoading && (!items || items.length === 0);

  const trackRef = useRef<HTMLDivElement>(null);
  const [loopWidth, setLoopWidth] = useState(0);

  const fingerprint = useMemo(() => items?.map(i => i.title).join('|') ?? '', [items]);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || !items?.length) return;
    const measure = () => setLoopWidth(el.scrollWidth / 2);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fingerprint]);

  const scrollDuration = Math.max(30, (items?.length ?? 0) * 15);

  if (isLoading) {
    return (
      <div className="px-3.5 py-2 overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.4)' }}>
            ⚡ Loading Bitcoin news...
          </span>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="px-3.5 py-2 overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.35)' }}>
            📡 Live data · mempool.space + coingecko
          </span>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onShowNews}
      className="w-full text-left px-3.5 py-2 overflow-hidden transition-colors hover:bg-white/[0.02] active:bg-white/[0.04]"
    >
      <div className="flex items-center gap-2">
        <motion.div
          className="w-1 h-1 rounded-full shrink-0"
          style={{ background: 'hsl(var(--bitcoin) / 0.5)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-[8px] font-body font-semibold tracking-widest uppercase shrink-0" style={{ color: 'hsl(var(--bitcoin) / 0.75)' }}>
          News
        </span>
        <div className="flex-1 overflow-hidden">
          <div
            ref={trackRef}
            className="flex whitespace-nowrap w-max"
            style={loopWidth > 0 ? {
              animation: `ticker-slide ${scrollDuration}s linear infinite`,
            } : undefined}
          >
            {[...items!, ...items!].map((item, i) => {
              const src = item.source.length > 16 ? item.source.slice(0, 15) + '…' : item.source;
              const label = `${src}  ·  ${item.title}`;
              return (
                <span key={`${i}-${item.title}`} className="inline-flex items-center shrink-0">
                  <span className="text-[11px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.55)' }}>
                    {label}
                  </span>
                  <span className="text-[10px] mx-2.5" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>⚡</span>
                </span>
              );
            })}
          </div>
        </div>
        <span
          className="text-[10px] shrink-0 tracking-wider uppercase font-body font-semibold px-2 py-0.5 rounded-full border"
          style={{
            color: 'hsl(var(--bitcoin))',
            backgroundColor: 'hsl(var(--bitcoin) / 0.08)',
            borderColor: 'hsl(var(--bitcoin) / 0.25)',
            textShadow: '0 0 8px hsl(var(--bitcoin) / 0.15)',
          }}
        >
          ▸
        </span>
      </div>
      {loopWidth > 0 && (
        <style>{`@keyframes ticker-slide { from { transform: translateX(0); } to { transform: translateX(-${loopWidth}px); } }`}</style>
      )}
    </button>
  );
}

// ── Macro color dot helper ──

function MacroDot({ displayMacroState }: { displayMacroState: string }) {
  const bg = displayMacroState === 'DEEP_VALUE' ? 'hsl(var(--energy))' :
    displayMacroState === 'ACCUMULATION' ? 'hsl(var(--success))' :
    displayMacroState === 'WARM' ? 'hsl(var(--bitcoin))' :
    displayMacroState === 'HOT' ? 'hsl(var(--stress))' :
    displayMacroState === 'MIXED' ? 'hsl(var(--mood))' :
    'hsl(var(--foreground) / 0.25)';
  const shadow = displayMacroState !== 'NEUTRAL'
    ? `0 0 4px ${displayMacroState === 'DEEP_VALUE' ? 'hsl(var(--energy) / 0.4)' :
        displayMacroState === 'ACCUMULATION' ? 'hsl(var(--success) / 0.4)' :
        displayMacroState === 'WARM' ? 'hsl(var(--bitcoin) / 0.4)' :
        displayMacroState === 'HOT' ? 'hsl(var(--stress) / 0.4)' :
        'hsl(var(--mood) / 0.3)'}`
    : 'none';

  return (
    <div className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: bg, boxShadow: shadow }} />
  );
}

// ── Main Footer ──

export default function AppFooter({
  network, matrixMode, newsItems, macro,
  stackSatsPhase, stackSatsLoading, worldDimming, sceneRef,
  onMatrixEnter, onMatrixExit, onStackSatsStart,
  onSetStackSatsLoading, onShowDonation, onRefreshNews,
  thoughtTickerProps,
}: AppFooterProps) {
  const [newsOpen, setNewsOpen] = useState(false);

  return (
    <>
    <NewsSheet open={newsOpen} onClose={() => setNewsOpen(false)} newsItems={newsItems} onRefresh={onRefreshNews} />
    <motion.footer data-share-exclude="true" className="w-full max-w-sm mx-auto px-3 pb-3 pt-0 relative z-10" style={{ position: 'sticky', bottom: 0 }}
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: worldDimming ? 0.4 : 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: worldDimming ? 0.6 : 0.3 }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {/* Thought ticker — above pill buttons */}
        {!matrixMode && thoughtTickerProps && (
          <div className="flex items-center justify-center pb-2">
            <div className="flex-1 min-w-0">
              <BuddyThoughtTicker
                mood={thoughtTickerProps.mood}
                weather={thoughtTickerProps.weather}
                networkState={thoughtTickerProps.networkState}
                blockState={thoughtTickerProps.blockState}
                priceState={thoughtTickerProps.priceState}
                verdict={thoughtTickerProps.verdict}
                sentimentBucket={thoughtTickerProps.sentimentBucket}
                activeSignature={thoughtTickerProps.activeSignature}
                blocksToHalving={thoughtTickerProps.blocksToHalving}
                hashrateChange7d={thoughtTickerProps.hashrateChange7d}
                lightningVitalityScore={thoughtTickerProps.lightningVitalityScore}
                footerLine={thoughtTickerProps.footerLine}
                macroState={thoughtTickerProps.macroState}
                seasonState={thoughtTickerProps.seasonState}
              />
            </div>
          </div>
        )}

        {/* Stack Sats button — matrix mode only */}
        {matrixMode && stackSatsPhase === 'idle' && (
          <div className="pb-2">
            <button
              onClick={async () => {
                onSetStackSatsLoading(true);
                await import('@/components/StackSatsGame').catch(() => {});
                onSetStackSatsLoading(false);
                onStackSatsStart();
              }}
              disabled={stackSatsLoading}
              className="w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.2em] transition-all duration-200 active:scale-95 disabled:opacity-70"
              style={{
                fontFamily: 'monospace',
                color: 'rgba(0,255,70,0.9)',
                background: 'rgba(0,255,70,0.04)',
                border: '1px solid rgba(0,255,70,0.15)',
                boxShadow: '0 0 24px rgba(0,255,70,0.06), inset 0 1px 0 rgba(0,255,70,0.05)',
                textShadow: '0 0 12px rgba(0,255,70,0.4)',
                backdropFilter: 'blur(12px)',
                animation: stackSatsLoading ? 'none' : 'stack-sats-glow 2.5s ease-in-out infinite, stack-sats-border 3s ease-in-out infinite',
              }}
            >
              {stackSatsLoading ? (
                <>
                  <span className="animate-spin" style={{ fontSize: '16px' }}>⟳</span>
                  Loading…
                </>
              ) : (
                <>
                  <span className="animate-[stack-sats-icon_3s_ease-in-out_infinite]" style={{ fontSize: '16px' }}>⛏️</span>
                  Build a Block
                  <span className="text-[8px] tracking-wider" style={{ color: 'rgba(0,255,70,0.4)' }}>▶</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Pill choice area */}
        <div className="flex items-stretch gap-0">
          {matrixMode ? (
            <motion.button
              onClick={onMatrixExit}
              className="group flex-1 py-3.5 px-4 rounded-xl flex items-center justify-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.2em] transition-all duration-200 active:scale-[0.97]"
              style={{
                fontFamily: 'monospace',
                color: 'rgba(60,130,246,0.95)',
                background: 'rgba(60,130,246,0.05)',
                border: '1px solid rgba(60,130,246,0.2)',
                boxShadow: '0 0 30px rgba(60,130,246,0.1), 0 4px 20px rgba(60,130,246,0.05), inset 0 1px 0 rgba(60,130,246,0.08)',
                textShadow: '0 0 16px rgba(60,130,246,0.5)',
                backdropFilter: 'blur(16px)',
                animation: 'exit-matrix-border 3s ease-in-out infinite',
              }}
              whileHover={{ boxShadow: '0 0 40px rgba(60,130,246,0.2), 0 4px 24px rgba(60,130,246,0.1), inset 0 1px 0 rgba(60,130,246,0.1)' }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="inline-block w-[22px] h-[10px] rounded-full animate-pulse" style={{ background: 'linear-gradient(180deg, hsl(210 90% 75%) 0%, hsl(210 85% 55%) 50%, hsl(210 80% 40%) 100%)', boxShadow: '0 0 12px rgba(60,130,246,0.8), 0 0 24px rgba(60,130,246,0.4), 0 0 40px rgba(60,130,246,0.15), inset 0 2px 3px hsl(210 90% 82% / 0.4), inset 0 -1px 2px hsl(210 80% 30% / 0.5)' }} />
              exit matrix
            </motion.button>
          ) : (
            <>
              {/* Red pill — Enter the Matrix */}
              <motion.button
                onClick={onMatrixEnter}
                className="flex-1 py-3.5 px-3 rounded-xl flex flex-col items-center justify-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.15em] transition-all duration-200 relative overflow-hidden"
                style={{
                  fontFamily: 'monospace',
                  color: 'hsl(0 68% 45%)',
                  background: 'linear-gradient(145deg, hsl(0 0% 100% / 0.82), hsl(0 0% 96% / 0.72))',
                  border: '1px solid hsl(0 68% 50% / 0.2)',
                  boxShadow: '0 0 30px hsl(0 68% 50% / 0.08), 0 4px 20px hsl(0 68% 50% / 0.04), inset 0 1px 0 hsl(0 0% 100% / 0.6), inset 0 -1px 0 hsl(0 0% 100% / 0.2), inset 0 0 12px hsl(0 0% 100% / 0.15)',
                  textShadow: '0 0 16px hsl(0 68% 50% / 0.5)',
                  backdropFilter: 'blur(16px)',
                  animation: 'pill-pulse 2.5s ease-in-out infinite',
                }}
                whileHover={{ scale: 1.04, boxShadow: '0 0 50px hsl(0 68% 50% / 0.25), 0 4px 30px hsl(0 68% 50% / 0.15), inset 0 1px 0 hsl(0 68% 50% / 0.12)', borderColor: 'hsl(0 68% 50% / 0.35)' }}
                whileTap={{ scale: 0.93 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <span className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, hsl(0 68% 70% / 0.12) 45%, hsl(0 68% 80% / 0.18) 50%, hsl(0 68% 70% / 0.12) 55%, transparent 60%)', backgroundSize: '250% 100%', animation: 'pill-shimmer 4s ease-in-out infinite', borderRadius: 'inherit' }} />
                <span className="inline-block w-[22px] h-[10px] rounded-full relative z-[1]" style={{ background: 'linear-gradient(180deg, hsl(0 68% 72%) 0%, hsl(0 68% 55%) 50%, hsl(0 68% 42%) 100%)', boxShadow: '0 0 12px hsl(0 68% 50% / 0.8), 0 0 24px hsl(0 68% 50% / 0.4), 0 0 40px hsl(0 68% 50% / 0.15), inset 0 2px 3px hsl(0 68% 80% / 0.4), inset 0 -1px 2px hsl(0 68% 30% / 0.5)', animation: 'pill-pulse 2.5s ease-in-out infinite' }} />
                <span className="relative z-[1]">Enter Matrix</span>
              </motion.button>

              {/* Choose wisely divider */}
              <motion.div
                className="flex flex-col items-center justify-center shrink-0 px-1"
                animate={{ opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <span className="text-[5px] font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'monospace', color: 'hsl(var(--foreground) / 0.25)', textShadow: '0 0 6px hsl(var(--foreground) / 0.06)', writingMode: 'vertical-lr' }}>
                  or
                </span>
              </motion.div>

              {/* Blue pill — View News */}
              <motion.button
                onClick={() => setNewsOpen(true)}
                className="flex-1 py-3.5 px-3 rounded-xl flex flex-col items-center justify-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.15em] transition-all duration-200 relative overflow-hidden"
                style={{
                  fontFamily: 'monospace',
                  color: 'rgba(40,110,230,0.95)',
                  background: 'linear-gradient(145deg, hsl(0 0% 100% / 0.82), hsl(0 0% 96% / 0.72))',
                  border: '1px solid rgba(60,130,246,0.2)',
                  boxShadow: '0 0 30px rgba(60,130,246,0.08), 0 4px 20px rgba(60,130,246,0.04), inset 0 1px 0 hsl(0 0% 100% / 0.6), inset 0 -1px 0 hsl(0 0% 100% / 0.2), inset 0 0 12px hsl(0 0% 100% / 0.15)',
                  textShadow: '0 0 16px rgba(60,130,246,0.5)',
                  backdropFilter: 'blur(16px)',
                  animation: 'exit-matrix-border 3s ease-in-out infinite',
                }}
                whileHover={{ scale: 1.04, boxShadow: '0 0 50px rgba(60,130,246,0.25), 0 4px 30px rgba(60,130,246,0.15), inset 0 1px 0 rgba(60,130,246,0.12)', borderColor: 'rgba(60,130,246,0.35)' }}
                whileTap={{ scale: 0.93 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <span className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(60,130,246,0.12) 45%, rgba(100,160,255,0.18) 50%, rgba(60,130,246,0.12) 55%, transparent 60%)', backgroundSize: '250% 100%', animation: 'pill-shimmer 4s ease-in-out 1s infinite', borderRadius: 'inherit' }} />
                <span className="inline-block w-[22px] h-[10px] rounded-full relative z-[1]" style={{ background: 'linear-gradient(180deg, hsl(210 90% 75%) 0%, hsl(210 85% 55%) 50%, hsl(210 80% 40%) 100%)', boxShadow: '0 0 12px rgba(60,130,246,0.8), 0 0 24px rgba(60,130,246,0.4), 0 0 40px rgba(60,130,246,0.15), inset 0 2px 3px hsl(210 90% 82% / 0.4), inset 0 -1px 2px hsl(210 80% 30% / 0.5)', animation: 'exit-matrix-border 3s ease-in-out infinite' }} />
                <span className="relative z-[1]">View News</span>
              </motion.button>
            </>
          )}
        </div>
      </motion.div>

      {/* Live data attribution — outside capsule */}
      <div className="flex items-center justify-center gap-1.5 pt-1.5 pb-0.5">
        <MacroDot displayMacroState={macro.state} />
        <p className="text-[7px] font-body font-light tracking-wide" style={{ color: 'hsl(var(--foreground) / 0.22)' }}>
          live data · mempool.space + coingecko
        </p>
        <span style={{ color: 'hsl(var(--foreground) / 0.12)' }} className="text-[7px]">·</span>
        <motion.button
          onClick={() => onShowDonation()}
          className="text-[8px] font-body font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border"
          style={{
            color: 'hsl(var(--bitcoin))',
            backgroundColor: 'hsl(var(--bitcoin) / 0.08)',
            borderColor: 'hsl(var(--bitcoin) / 0.25)',
            textShadow: '0 0 8px hsl(var(--bitcoin) / 0.15)',
          }}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ₿ Feed Buddy
        </motion.button>
        <span style={{ color: 'hsl(var(--foreground) / 0.12)' }} className="text-[7px]">·</span>
        <a
          href="https://x.com/MempoolBuddy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[8px] font-body font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border transition-opacity hover:opacity-80"
          style={{
            color: 'hsl(var(--foreground) / 0.6)',
            backgroundColor: 'hsl(var(--foreground) / 0.05)',
            borderColor: 'hsl(var(--foreground) / 0.15)',
          }}
        >
          𝕏
        </a>
      </div>
    </motion.footer>
    </>
  );
}
